import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { createCampaign, getCampaign, updateCampaign } from '../api/campaigns';
import { listPacksV3 } from '../api/knowledgePacksV3';
import ChipTag from '../components/ui/ChipTag';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_TYPOGRAPHY, getButtonStyle } from '../styles/emziCI';

const CampaignWizardCompact = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Accordion state
  const [openSection, setOpenSection] = useState(1);
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    campaign_name: '',
    campaign_type: 'regional',
    description: '',
    base_product_pack_id: '',
    base_product_name: '',
    promotion_pack_id: null,
    timeline: {
      start_date: '',
      end_date: ''
    },
    assigned_teams: [],
    status: 'draft'
  });

  // Available data
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    loadData();
    const productIdParam = searchParams.get('product_id');
    if (productIdParam) {
      setFormData(prev => ({ ...prev, base_product_pack_id: productIdParam }));
      const selectedProduct = products.find(p => p.pack_id === productIdParam);
      if (selectedProduct) {
        setFormData(prev => ({ ...prev, base_product_name: selectedProduct.title }));
      }
    }
  }, [searchParams, products]);

  const loadData = async () => {
    try {
      // Load products (type: product)
      const productsResponse = await listPacksV3({ pack_type: 'product', status: 'published' });
      setProducts(productsResponse.packs || []);

      // Load promotions (type: promotion)
      const promotionsResponse = await listPacksV3({ pack_type: 'promotion', status: 'published' });
      setPromotions(promotionsResponse.packs || []);

      // Mock groups for now - in production, fetch from API
      setGroups([
        { group_id: 'g1', name: 'Sales Team North', region: 'North', member_count: 12 },
        { group_id: 'g2', name: 'Sales Team South', region: 'South', member_count: 15 },
        { group_id: 'g3', name: 'Customer Support', region: 'All', member_count: 8 }
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
  };

  const validateSection = (section) => {
    const errors = {};
    
    if (section === 1) {
      if (!formData.base_product_pack_id) errors.product = 'Product is required';
      if (!formData.campaign_name || formData.campaign_name.trim().length < 3) {
        errors.name = 'Campaign name must be at least 3 characters';
      }
    }
    
    if (section === 2) {
      if (!formData.promotion_pack_id) errors.promotion = 'Promotion is required';
      if (!formData.timeline.start_date) errors.start_date = 'Start date is required';
      if (!formData.timeline.end_date) errors.end_date = 'End date is required';
      if (formData.timeline.start_date && formData.timeline.end_date) {
        if (new Date(formData.timeline.end_date) <= new Date(formData.timeline.start_date)) {
          errors.end_date = 'End date must be after start date';
        }
      }
    }
    
    if (section === 3) {
      if (formData.status === 'active' && formData.assigned_teams.length === 0) {
        errors.teams = 'At least 1 team required to activate';
      }
    }
    
    return errors;
  };

  const toggleTeam = (groupId) => {
    setFormData(prev => {
      const isSelected = prev.assigned_teams.includes(groupId);
      return {
        ...prev,
        assigned_teams: isSelected
          ? prev.assigned_teams.filter(id => id !== groupId)
          : [...prev.assigned_teams, groupId]
      };
    });
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const draftData = { ...formData, status: 'draft' };
      if (campaignId) {
        await updateCampaign(campaignId, draftData);
        toast.success('Draft saved');
      } else {
        const response = await createCampaign(draftData);
        toast.success('Draft saved');
        navigate(`/campaigns/${response.campaign_id}`);
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateAndAssign = async () => {
    // Validate all sections
    const errors1 = validateSection(1);
    const errors2 = validateSection(2);
    const errors3 = validateSection(3);
    
    if (Object.keys(errors1).length > 0 || Object.keys(errors2).length > 0 || Object.keys(errors3).length > 0) {
      toast.error('Please fix all errors before creating');
      return;
    }

    setIsSaving(true);
    try {
      const activeData = { ...formData, status: 'active' };
      if (campaignId) {
        await updateCampaign(campaignId, activeData);
        toast.success('Campaign updated and activated');
      } else {
        const response = await createCampaign(activeData);
        toast.success('Campaign created and activated');
        navigate(`/campaigns/${response.campaign_id}`);
      }
    } catch (error) {
      console.error('Failed to create campaign:', error);
      toast.error(error.message || 'Failed to create campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const AccordionSection = ({ number, title, children, isValid }) => {
    const isOpen = openSection === number;
    const errors = validateSection(number);
    const hasErrors = Object.keys(errors).length > 0;

    return (
      <div 
        style={{
          border: `1px solid ${hasErrors && touchedFields[`section${number}`] ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          marginBottom: EMZI_SPACING.sm,
          backgroundColor: EMZI_COLORS.background
        }}
      >
        <button
          onClick={() => {
            setOpenSection(isOpen ? 0 : number);
            setTouchedFields(prev => ({ ...prev, [`section${number}`]: true }));
          }}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: EMZI_SPACING.compact,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: EMZI_SPACING.sm }}>
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: hasErrors ? EMZI_COLORS.error : EMZI_COLORS.primary,
                color: EMZI_COLORS.textWhite,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              {number}
            </div>
            <span style={{ ...EMZI_TYPOGRAPHY.h2, fontSize: '15px', fontWeight: '600' }}>
              {title}
            </span>
          </div>
          <span style={{ fontSize: '18px', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
            ▼
          </span>
        </button>
        {isOpen && (
          <div style={{ padding: EMZI_SPACING.compact, paddingTop: 0 }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  const errors1 = validateSection(1);
  const errors2 = validateSection(2);
  const errors3 = validateSection(3);

  return (
    <div style={{ 
      backgroundColor: EMZI_COLORS.background, 
      minHeight: '100vh',
      paddingBottom: '80px' // Space for sticky bottom bar
    }}>
      {/* Header */}
      <div 
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: EMZI_COLORS.background,
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          padding: EMZI_SPACING.compact,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/campaigns')}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: EMZI_COLORS.primary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ←
          </button>
          <h1 style={{ ...EMZI_TYPOGRAPHY.title, margin: 0, fontSize: '18px' }}>
            {campaignId ? 'Edit Campaign' : 'Create Campaign'}
          </h1>
          <div style={{ width: '32px' }} /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Accordion Sections */}
      <div style={{ padding: EMZI_SPACING.compact }}>
        {/* Section 1: Product & Info */}
        <AccordionSection number={1} title="Product & Campaign Info">
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
            {/* Product Select */}
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                Base Product <span style={{ color: EMZI_COLORS.error }}>*</span>
              </label>
              <select
                value={formData.base_product_pack_id}
                onChange={(e) => {
                  handleFieldChange('base_product_pack_id', e.target.value);
                  const selected = products.find(p => p.pack_id === e.target.value);
                  if (selected) handleFieldChange('base_product_name', selected.title);
                }}
                onBlur={() => handleFieldBlur('product')}
                style={{
                  width: '100%',
                  minHeight: '40px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${touchedFields.product && errors1.product ? EMZI_COLORS.error : EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background
                }}
              >
                <option value="">Select product...</option>
                {products.map(p => (
                  <option key={p.pack_id} value={p.pack_id}>{p.title}</option>
                ))}
              </select>
              {touchedFields.product && errors1.product && (
                <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
                  {errors1.product}
                </span>
              )}
            </div>

            {/* Campaign Name */}
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                Campaign Name <span style={{ color: EMZI_COLORS.error }}>*</span>
              </label>
              <input
                type="text"
                value={formData.campaign_name}
                onChange={(e) => handleFieldChange('campaign_name', e.target.value)}
                onBlur={() => handleFieldBlur('name')}
                placeholder="e.g., Q1 2025 Promo"
                style={{
                  width: '100%',
                  minHeight: '40px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${touchedFields.name && errors1.name ? EMZI_COLORS.error : EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background
                }}
              />
              {touchedFields.name && errors1.name && (
                <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
                  {errors1.name}
                </span>
              )}
            </div>

            {/* Campaign Type */}
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                Campaign Type
              </label>
              <select
                value={formData.campaign_type}
                onChange={(e) => handleFieldChange('campaign_type', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '40px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background
                }}
              >
                <option value="regional">Regional</option>
                <option value="national">National</option>
                <option value="pilot">Pilot</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Brief campaign description..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background,
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        </AccordionSection>

        {/* Section 2: Promotion & Timeline */}
        <AccordionSection number={2} title="Promotion & Timeline">
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
            {/* Promotion Select */}
            <div>
              <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                Promotion Pack <span style={{ color: EMZI_COLORS.error }}>*</span>
              </label>
              <select
                value={formData.promotion_pack_id || ''}
                onChange={(e) => handleFieldChange('promotion_pack_id', e.target.value)}
                onBlur={() => handleFieldBlur('promotion')}
                style={{
                  width: '100%',
                  minHeight: '40px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: `1px solid ${touchedFields.promotion && errors2.promotion ? EMZI_COLORS.error : EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  backgroundColor: EMZI_COLORS.background
                }}
              >
                <option value="">Select promotion...</option>
                {promotions.map(p => (
                  <option key={p.pack_id} value={p.pack_id}>{p.title}</option>
                ))}
              </select>
              {touchedFields.promotion && errors2.promotion && (
                <span style={{ fontSize: '11px', color: EMZI_COLORS.error, marginTop: EMZI_SPACING.xs, display: 'block' }}>
                  {errors2.promotion}
                </span>
              )}
              <button
                onClick={() => toast.info('Quick promotion creation coming soon')}
                style={{
                  marginTop: EMZI_SPACING.xs,
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: EMZI_COLORS.primary,
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                + Quick create promotion
              </button>
            </div>

            {/* Date Range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: EMZI_SPACING.sm }}>
              <div>
                <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                  Start Date <span style={{ color: EMZI_COLORS.error }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.timeline.start_date}
                  onChange={(e) => handleFieldChange('timeline', { ...formData.timeline, start_date: e.target.value })}
                  onBlur={() => handleFieldBlur('start_date')}
                  style={{
                    width: '100%',
                    minHeight: '40px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: `1px solid ${touchedFields.start_date && errors2.start_date ? EMZI_COLORS.error : EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    backgroundColor: EMZI_COLORS.background
                  }}
                />
                {touchedFields.start_date && errors2.start_date && (
                  <span style={{ fontSize: '10px', color: EMZI_COLORS.error, marginTop: '2px', display: 'block' }}>
                    {errors2.start_date}
                  </span>
                )}
              </div>
              <div>
                <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
                  End Date <span style={{ color: EMZI_COLORS.error }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.timeline.end_date}
                  onChange={(e) => handleFieldChange('timeline', { ...formData.timeline, end_date: e.target.value })}
                  onBlur={() => handleFieldBlur('end_date')}
                  style={{
                    width: '100%',
                    minHeight: '40px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: `1px solid ${touchedFields.end_date && errors2.end_date ? EMZI_COLORS.error : EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    backgroundColor: EMZI_COLORS.background
                  }}
                />
                {touchedFields.end_date && errors2.end_date && (
                  <span style={{ fontSize: '10px', color: EMZI_COLORS.error, marginTop: '2px', display: 'block' }}>
                    {errors2.end_date}
                  </span>
                )}
              </div>
            </div>

            {/* Info hint */}
            <div style={{ 
              backgroundColor: '#FEF3C7', 
              padding: EMZI_SPACING.sm, 
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '12px',
              color: '#92400E'
            }}>
              ℹ️ Teams will lose access automatically when the campaign expires.
            </div>
          </div>
        </AccordionSection>

        {/* Section 3: Assign Teams */}
        <AccordionSection number={3} title="Assign Teams">
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.md }}>
            {/* Team count summary */}
            <div style={{ ...EMZI_TYPOGRAPHY.body, fontSize: '13px', color: EMZI_COLORS.textSecondary }}>
              Assigned: <strong>{formData.assigned_teams.length}</strong> team{formData.assigned_teams.length !== 1 ? 's' : ''}
            </div>

            {/* Team selection */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.xs }}>
              {groups.map(group => (
                <label
                  key={group.group_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: EMZI_SPACING.sm,
                    border: `1px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.sm,
                    cursor: 'pointer',
                    backgroundColor: formData.assigned_teams.includes(group.group_id) ? '#EFF6FF' : EMZI_COLORS.background
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.assigned_teams.includes(group.group_id)}
                    onChange={() => toggleTeam(group.group_id)}
                    style={{ marginRight: EMZI_SPACING.sm }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{group.name}</div>
                    <div style={{ fontSize: '11px', color: EMZI_COLORS.textMuted }}>
                      {group.region} • {group.member_count} members
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {/* Selected teams as chips */}
            {formData.assigned_teams.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: EMZI_SPACING.xs }}>
                {formData.assigned_teams.map(teamId => {
                  const team = groups.find(g => g.group_id === teamId);
                  return team ? (
                    <ChipTag 
                      key={teamId}
                      label={team.name}
                      onRemove={() => toggleTeam(teamId)}
                    />
                  ) : null;
                })}
              </div>
            )}

            {touchedFields.section3 && errors3.teams && (
              <span style={{ fontSize: '11px', color: EMZI_COLORS.error }}>
                {errors3.teams}
              </span>
            )}
          </div>
        </AccordionSection>
      </div>

      {/* Sticky Bottom Bar */}
      <div 
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: EMZI_COLORS.background,
          borderTop: `1px solid ${EMZI_COLORS.border}`,
          padding: EMZI_SPACING.compact,
          display: 'flex',
          gap: EMZI_SPACING.sm,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
          zIndex: 20
        }}
      >
        <button
          onClick={handleSaveDraft}
          disabled={isSaving}
          style={{
            ...getButtonStyle('outlined'),
            flex: 1,
            fontSize: '13px',
            minHeight: '40px'
          }}
        >
          {isSaving ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          onClick={handleCreateAndAssign}
          disabled={isSaving}
          style={{
            ...getButtonStyle('cta'),
            flex: 2,
            fontSize: '13px',
            minHeight: '40px'
          }}
        >
          {isSaving ? 'Creating...' : 'Create & Assign'}
        </button>
      </div>
    </div>
  );
};

export default CampaignWizardCompact;
